import React from 'react';
import classNames from 'classnames';
import { useTour } from '../core/TourProvider';
import styles from '../stylesheets/basicHeaderFields.module.scss';
import { ChevronDownIcon } from '../icons/TourIcons';

interface BasicHeaderFieldsProps {
  name?: string;
  subHeader?: string;
  avatarUrl?: string;
  balanceLabel?: string;
  balanceAmount?: number;
  autoPayOn?: boolean;
  actionsLabel?: string;
  previewButtonLabel?: string;
  previewButtonRef?: React.Ref<HTMLButtonElement>;
  onPreviewClick?: () => void;
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);

const BasicHeaderFields = ({
  name,
  subHeader,
  avatarUrl,
  balanceLabel,
  balanceAmount,
  autoPayOn,
  previewButtonLabel,
  previewButtonRef,
  onPreviewClick,
}: BasicHeaderFieldsProps) => {
  const { model } = useTour();
  const tenant = model.tenant;
  const resolvedName = name ?? tenant?.name ?? '';
  const resolvedSubHeader =
    subHeader ?? (tenant ? `${tenant.unitName} • ${tenant.propertyName}` : '');
  const resolvedAvatarUrl = avatarUrl ?? tenant?.avatarUrl;
  const resolvedBalanceLabel = balanceLabel ?? 'Balance';
  const resolvedBalanceAmount = balanceAmount ?? tenant?.balanceAmount ?? 0;
  const resolvedAutoPayOn = autoPayOn ?? tenant?.autoPayOn ?? false;
  const initials = getInitials(resolvedName || ' ');

  return (
    <div className={styles.headerContainer}>
      <div className={styles.headerRow}>
        <div className={styles.entityHeadersContainer}>
          <div className={styles.basicEntityInfo}>
            {resolvedAvatarUrl ? (
              <img
                className={styles.avatar}
                src={resolvedAvatarUrl}
                alt={`${resolvedName} avatar`}
              />
            ) : (
              <div className={styles.avatarFallback} aria-hidden="true">
                {initials}
              </div>
            )}
            <div className={styles.entityText}>
              <h2 className={styles.entityName}>{resolvedName}</h2>
              <p className={styles.entitySubHeader}>{resolvedSubHeader}</p>
            </div>
            <button
              type="button"
              className={styles.infoButton}
              aria-label="Entity details"
            >
              i
            </button>
          </div>
        </div>
        {/* <button type="button" className={styles.actionsMenu}>
          <span className={styles.actionsLabel}>{actionsLabel}</span>
          <span className={styles.actionsIcon}>
            <ChevronDownIcon />
          </span>
        </button> */}
      </div>
      {previewButtonLabel && onPreviewClick && (
        <button
          type="button"
          className={styles.previewButton}
          onClick={onPreviewClick}
          ref={previewButtonRef}
        >
          {previewButtonLabel}
          <span className={styles.previewButtonIcon}>
            <ChevronDownIcon />
          </span>
        </button>
      )}
      <div className={styles.balanceCardWrap}>
        <div className={styles.balanceCard}>
          <div className={styles.balanceSection}>
            <span className={styles.balanceLabel}>{resolvedBalanceLabel}</span>
            <span className={styles.balanceAmount}>
              {formatCurrency(resolvedBalanceAmount)}
            </span>
          </div>
          <button
            type="button"
            className={classNames(
              styles.autoPayButton,
              resolvedAutoPayOn ? styles.autoPayOn : styles.autoPayOff
            )}
            disabled
          >
            Auto Pay: {resolvedAutoPayOn ? 'On' : 'Off'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicHeaderFields;
